/* eslint-disable max-len */
import {HttpClientModule} from "@angular/common/http";
import {APP_INITIALIZER, ErrorHandler, NgModule} from "@angular/core";
import {BrowserModule} from "@angular/platform-browser";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {ClipboardModule} from "ngx-clipboard";
import {AppRoutingModule} from "./app-routing.module";
import {AppComponent} from "./app.component";
import {AppModule} from "./app.module";
import {ActionLogService} from "./features/action-log/action-log.service";
import {AnalysisService} from "./features/analize/analysis.service";
import {BloodPressureService} from "./features/analize/blood-pressure/bloodPressure.service";
import {GetSleepReportDataService} from "./features/analize/sleep-report/get-sleep-report-data.service";
import {FinishAnalysisService} from "./features/analize/finish-analysis.service";
import {MeasurementIdProvider} from "./features/analize/measurement-id-provider";
import {AuthGuard} from "./features/authentication/auth-guard.service";
import {AuthenticatedUserStore} from "./features/authentication/authenticated-user-store.service";
import {AuthenticationService} from "./features/authentication/authentication.service";
import {HomeGuard} from "./features/authentication/home-guard.service";
import {ChangePasswordService} from "./features/change-password/change-password.service";
import {CloseAnalysisService} from "./features/list/close-analysis.service";
import {DoctorAssignmentService} from "./features/list/doctor-assignment.service";
import {DoctorsService} from "./features/list/doctor-selector/doctors.service";
import {ListService} from "./features/list/list.service";
import {RejectStudyService} from "./features/list/reject/reject-study.service";
import {ReturnService} from "./features/list/return-study/return.service";
import {LoginService} from "./features/login/login.service";
import {AddNursesService} from "./features/add-nurses/add-nurses.service";
import {UploadService} from "./features/upload/upload.service";
import {UserListService} from "./features/user-list/user-list.service";
import {LocalizationsProvider} from "./i18n/localizations-provider";
import {ApiClient, productionBaseUrlProvider} from "./tools/api-client";
import {ErrorResponseHandler} from "./tools/error-response-handler";
import {ErrorIdProvider} from "./tools/errors/error-id-provider.service";
import {LocalStorageService} from "./tools/local-storage.service";
import {RouteNavigator} from "./tools/navigation/route-navigator.service";
import {Notifications} from "./tools/notifications/notifications";
import {Scheduler} from "./tools/notifications/scheduler";
import {ClockService} from "./tools/time/clock.service";
import {ChangeLanguageService} from "./features/change-language/change-language.service";
import {InitiateMeasurementService} from "./features/initiate-measurement/initiate-measurement.service";
import {OperatorHospitalsService} from "./features/initiate-measurement/operator-hospitals.service";
import {PrintOrderService} from "./features/print-order-form/print-order.service";
import {OrderPdfGeneratorService} from "./features/print-order-form/order-pdf-generator.service";
import {HospitalsService} from "./features/hospitals/hospitals.service";
import {EditHospitalService} from "./features/hospitals/edit-hospital/edit-hospital.service";
import {HospitalIdProvider} from "./features/hospitals/edit-hospital/hospital-id-provider";
import {PdfDownloaderService} from "./features/print-form/pdf-downloader.service";
import {PdfFillerService} from "./features/print-form/pdf-filler.service";
import {PrintFormService} from "./features/print-form/print-form.service";
import {EFormService} from "./features/e-forms/e-form.service";
import {environment} from "../environments/environment";
import {EformRoutingModule} from "./eform-routing.module";
import {EFormIdProvider} from "./features/e-forms/e-form-id-provider.service";
import {RECAPTCHA_SETTINGS, RecaptchaSettings} from "ng-recaptcha";
import {RecaptchaService} from "./features/e-forms/landing-page/recaptcha.service";
import {HospitalMeasurementTypesService} from "./features/hospitals/edit-hospital/hospital-measurement-types.service";
import {CommentsService} from "./features/comments/comments.service";
import {DownloadExternalFindingsService} from "./features/analize/ecg/download-external-findings.service";
import {DownloaderService} from "./tools/downloader.service";
import {TranslationFileReaderService} from "./features/translations/translation-file-reader.service";
import {EditMeasurementDataService} from "./features/list/edit-measurement/edit-measurement/edit-measurement-data.service";
import {AssignTechnicianService} from "./features/list/assign-technician/assign-technician.service";
import {Observable} from "rxjs";
import {tap} from "rxjs/operators";
import {AppInit, AppInitService, toLocalizationsMap} from "./tools/app-init/app-init.service";
import {ReleaseToggleState} from "./tools/release-toggles/release-toggle-state";
import {populateLocalizations} from "./i18n/localizations";
import {populateLanguages} from "./tools/languages";
import {DeleteMeasurementService} from "./features/list/delete-measurement/delete-measurement.service";
import {ReloadApp} from "./reload-app";
import {VersionMismatch} from "./version-mismatch";
import {RouteListener} from "./route-listener";
import {RestoreMeasurementService} from "./features/list/restore-measurement/restore-measurement.service";
import {EditHolterTypeService} from "./features/list/edit-holter-type/edit-holter-type.service";
import {ReassignDoctorService} from "./features/list/reassign-doctor/reassign-doctor.service";
import {EFormMailingInfoService} from "./features/e-forms/form/mailing-info/e-form-mailing-info.service";
import {RouteProvider} from "./features/initiate-measurement/route.provider";
import {MailDeviceService} from "./features/mail-device/mail-device.service";
import {EFormFinishMeasuringService} from "./features/e-forms/form/finish-measuring/e-form-finish-measuring.service";
import {ForgotPasswordService} from "./features/login/forgot-password/forgot-password.service";
import {TICK_INTERVAL_MS, Ticker} from "./tools/ticker";
import {RepeatMeasurementService} from "./features/list/repeat-measurement/repeat-measurement.service";
import {RevertFromDoneService} from "./features/list/revert-from-done/revert-from-done.service";
import {CurrentRoute} from "./tools/current-route";
import {ChainsService} from "./features/chains/chains.service";
import {ChainIdProvider} from "./features/chains/chain-details/chain-id-provider";
import {ChainDetailsService} from "./features/chains/chain-details/chain-details.service";
import {WindowService} from "./tools/window.service";
import {IS_TEST} from "./tools/test/test-token";
import {GeneratorSentencesService} from "./features/analize/ecg/generators/generator-sentences/generator-sentences.service";
import {TranslationService} from "./features/translations/translation.service";
import {ReleaseFeaturesService} from "./features/release-features/release-features.service";
import {ReassignTechnicianService} from "./features/list/list-item/list-item-action-buttons/reassign-technician/reassign-technician.service";
import {ReturnToPreanalysisService} from "./features/list/return-to-preanalysis/return-to-preanalysis.service";
import {MeasurementCodeGenerator} from "./features/initiate-measurement/measurement-code-generator";
import {TODO} from "./tools/test/todo";

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    environment.eforms ?
      EformRoutingModule :
      AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    AppModule
  ],
  providers: [
    BloodPressureService,
    UserListService,
    AddNursesService,
    ChangePasswordService,
    ListService,
    ActionLogService,
    UploadService,
    Notifications,
    RouteNavigator,
    Scheduler,
    LocalizationsProvider,
    TranslationService,
    ReleaseFeaturesService,
    DoctorsService,
    DoctorAssignmentService,
    LoginService,
    ClipboardModule,
    ApiClient,
    LoginService,
    LocalStorageService,
    AuthenticationService,
    AuthenticatedUserStore,
    AuthGuard,
    HomeGuard,
    HomeGuard,
    ErrorResponseHandler,
    ErrorHandler,
    ReturnService,
    MeasurementIdProvider,
    AnalysisService,
    GetSleepReportDataService,
    FinishAnalysisService,
    CloseAnalysisService,
    ErrorIdProvider,
    RejectStudyService,
    ClockService,
    InitiateMeasurementService,
    ChangeLanguageService,
    OperatorHospitalsService,
    PrintOrderService,
    OrderPdfGeneratorService,
    HospitalsService,
    EditHospitalService,
    HospitalIdProvider,
    PdfDownloaderService,
    PdfFillerService,
    PrintFormService,
    productionBaseUrlProvider(),
    EFormService,
    EFormIdProvider,
    DeleteMeasurementService,
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {siteKey: environment.recaptchaSiteKey} as RecaptchaSettings
    },
    RecaptchaService,
    HospitalMeasurementTypesService,
    CommentsService,
    DownloadExternalFindingsService,
    DownloaderService,
    TranslationFileReaderService,
    EditMeasurementDataService,
    AssignTechnicianService,
    AppInitService,
    {
      provide: APP_INITIALIZER,
      useFactory: loadAppInit,
      deps: [AppInitService, ApiClient],
      multi: true
    },
    ReloadApp,
    VersionMismatch,
    RouteListener,
    RestoreMeasurementService,
    EditHolterTypeService,
    ReassignDoctorService,
    EFormMailingInfoService,
    RouteProvider,
    MailDeviceService,
    EFormFinishMeasuringService,
    ForgotPasswordService,
    { provide: TICK_INTERVAL_MS, useValue: 1000 },
    Ticker,
    RepeatMeasurementService,
    RevertFromDoneService,
    CurrentRoute,
    ChainsService,
    ChainIdProvider,
    ChainDetailsService,
    WindowService,
    { provide: IS_TEST, useValue: false },
    GeneratorSentencesService,
    ReassignTechnicianService,
    ReturnToPreanalysisService,
    MeasurementCodeGenerator,
  ],
  bootstrap: [AppComponent],
})
export class ProductionAppModule {
}

function loadAppInit(appInitService: AppInitService): () => Observable<any> {
  return () => appInitService
    .getAppInit()
    .pipe(
      tap<AppInit>(appInit => {
        ReleaseToggleState.getInstance().setToggles(appInit.releaseToggles);
        populateLocalizations(toLocalizationsMap(appInit.localizations));
        populateLanguages(appInit.supportedLanguages);
      })
    );
}
